import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Card,
  Space,
  Button,
  Tag,
  Rate,
  Descriptions,
  Tabs,
  Image,
  Spin,
  Empty,
  Alert
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  MedicineBoxOutlined,
  PictureOutlined,
  SafetyOutlined,
  TeamOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import moment from "moment";
import _get from "lodash/get";

import BackButton from "components/BackButton";
import { ROLES } from "utils/constants";
import {
  getHealthPlaceDetailRequest,
  deleteHealthPlaceRequest,
  restoreHealthPlaceRequest,
} from "providers/HealthPlaceProvider/slice";
import "./styles.less";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const HealthDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail, isLoading } = useSelector((state) => state.healthPlace);
  const roleCurrentUser = useSelector((state) =>
    _get(state, "auth.currentUser.role")
  );

  const isAdminPermission = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(
    roleCurrentUser
  );

  useEffect(() => {
    if (id) {
      dispatch(getHealthPlaceDetailRequest({ id }));
    }
  }, [dispatch, id]);

  const handleEdit = () => {
    navigate(`/health/${id}/edit`);
  };

  const handleDelete = () => {
    dispatch(deleteHealthPlaceRequest({ _id: id }));
    setTimeout(() => {
      navigate("/health");
    }, 500);
  };

  const handleRestore = () => {
    dispatch(restoreHealthPlaceRequest({ _id: id }));
    dispatch(getHealthPlaceDetailRequest({ id }));
  };

  const renderWorkingHours = () => {
    if (!detail.workingHour || detail.workingHour.length === 0) {
      return <Text type="secondary">Chưa có thông tin</Text>;
    }

    const days = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];

    return (
      <div className="working-hours-list">
        {detail.workingHour.map((day, index) => (
          <div key={index} className="working-hour-item">
            <Text strong>{days[index]}: </Text>
            {day.alwaysClose ? (
              <Tag color="red">Đóng cửa</Tag>
            ) : day.alwaysOpen ? (
              <Tag color="green">Mở cả ngày</Tag>
            ) : day.is24Hours ? (
              <Tag color="blue">24/7</Tag>
            ) : day.time && day.time.length > 0 ? (
              <Space>
                {day.time.map((slot, idx) => (
                  <Text key={idx}>
                    {slot.from} - {slot.to}
                  </Text>
                ))}
              </Space>
            ) : (
              <Text type="secondary">Không có thông tin</Text>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading && !detail._id) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!detail._id) {
    return (
      <div className="health-detail-wrapper">
        <BackButton title="Chi tiết cơ sở y tế" />
        <Empty description="Không tìm thấy cơ sở y tế" />
      </div>
    );
  }

  return (
    <div className="health-detail-wrapper">
      <Row className="health-detail-header" align="middle" justify="space-between">
        <BackButton title="Chi tiết cơ sở y tế" />
        {isAdminPermission && (
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              Chỉnh sửa
            </Button>
            {detail.isDeleted ? (
              <Button
                icon={<DeleteOutlined />}
                onClick={handleRestore}
                style={{ color: "#52c41a", borderColor: "#52c41a" }}
              >
                Khôi phục
              </Button>
            ) : (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                Xóa
              </Button>
            )}
          </Space>
        )}
      </Row>

      {detail.isDeleted && (
        <Alert
          message="Cơ sở y tế này đã bị xóa"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card loading={isLoading}>
        <Tabs defaultActiveKey="info">
          <TabPane tab="Thông tin chung" key="info">
            <Row gutter={[24, 24]}>
              <Col span={8}>
                {detail.thumbnail ? (
                  <Image
                    src={detail.thumbnail}
                    alt={detail.placeName}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                ) : (
                  <div className="no-image-placeholder">
                    <PictureOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />
                    <Text type="secondary">Chưa có hình ảnh</Text>
                  </div>
                )}
              </Col>

              <Col span={16}>
                <Descriptions title={detail.placeName} column={1} bordered>
                  <Descriptions.Item label="ID">
                    #{detail.healthPlaceId || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Loại y tế">
                    {Array.isArray(detail.healthType) && detail.healthType.length > 0 ? (
                      detail.healthType.map((type, index) => (
                        <Tag color="blue" key={type._id || index} style={{ marginBottom: 4 }}>
                          {type.title}
                        </Tag>
                      ))
                    ) : (
                      <Tag color="default">N/A</Tag>
                    )}
                  </Descriptions.Item>

                  {detail.emergencyService && (
                    <Descriptions.Item label="Cấp cứu">
                      <Tag color="red" icon={<MedicineBoxOutlined />}>
                        Có dịch vụ cấp cứu 24/7
                      </Tag>
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item label="Địa chỉ">
                    <Space>
                      <EnvironmentOutlined />
                      {detail.address}
                    </Space>
                  </Descriptions.Item>

                  {(detail.prefecture || detail.district || detail.ward) && (
                    <Descriptions.Item label="Khu vực">
                      <Space>
                        {detail.prefecture && <Tag>{detail.prefecture}</Tag>}
                        {detail.district && <Tag>{detail.district}</Tag>}
                        {detail.ward && <Tag>{detail.ward}</Tag>}
                      </Space>
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item label="Điện thoại">
                    <Space>
                      <PhoneOutlined />
                      {detail.phone || "Chưa có"}
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Email">
                    <Space>
                      <MailOutlined />
                      {detail.email || "Chưa có"}
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Website">
                    {detail.website ? (
                      <a href={detail.website} target="_blank" rel="noopener noreferrer">
                        <Space>
                          <GlobalOutlined />
                          {detail.website}
                        </Space>
                      </a>
                    ) : (
                      "Chưa có"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label="Đánh giá">
                    <Space direction="vertical">
                      <Rate disabled value={detail.rating} allowHalf />
                      <Text type="secondary">
                        {detail.rating}/5 ({detail.totalNumberOfRatings} lượt đánh giá)
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Mô tả">
                    {detail.description || <Text type="secondary">Chưa có mô tả</Text>}
                  </Descriptions.Item>

                  <Descriptions.Item label="Ngày tạo">
                    {detail.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Dịch vụ & Tiện ích" key="services">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title={<Space><SafetyOutlined />Dịch vụ cung cấp</Space>} size="small">
                  {detail.services && detail.services.length > 0 ? (
                    <Space wrap>
                      {detail.services.map((service, index) => (
                        <Tag key={index} color="blue">{service}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">Chưa có thông tin</Text>
                  )}
                </Card>
              </Col>

              <Col span={12}>
                <Card title="Chuyên khoa" size="small">
                  {detail.specialties && detail.specialties.length > 0 ? (
                    <Space wrap>
                      {detail.specialties.map((specialty, index) => (
                        <Tag key={index} color="green">{specialty}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">Chưa có thông tin</Text>
                  )}
                </Card>
              </Col>

              <Col span={12}>
                <Card title="Tiện ích" size="small">
                  {detail.facilities && detail.facilities.length > 0 ? (
                    <Space wrap>
                      {detail.facilities.map((facility, index) => (
                        <Tag key={index}>{facility}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">Chưa có thông tin</Text>
                  )}
                </Card>
              </Col>

              <Col span={12}>
                <Card title="Ngôn ngữ hỗ trợ" size="small">
                  {detail.languagesSupported && detail.languagesSupported.length > 0 ? (
                    <Space wrap>
                      {detail.languagesSupported.map((lang, index) => (
                        <Tag key={index} color="purple">{lang}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">Chưa có thông tin</Text>
                  )}
                </Card>
              </Col>

              <Col span={12}>
                <Card title="Bảo hiểm chấp nhận" size="small">
                  {detail.insuranceAccepted && detail.insuranceAccepted.length > 0 ? (
                    <Space wrap>
                      {detail.insuranceAccepted.map((insurance, index) => (
                        <Tag key={index} color="orange">{insurance}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">Chưa có thông tin</Text>
                  )}
                </Card>
              </Col>

              <Col span={12}>
                <Card title="Phương thức thanh toán" size="small">
                  {detail.paymentMethods && detail.paymentMethods.length > 0 ? (
                    <Space wrap>
                      {detail.paymentMethods.map((method, index) => (
                        <Tag key={index} color="cyan">{method}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">Chưa có thông tin</Text>
                  )}
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Nhân sự & Chi phí" key="staff">
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card>
                  <Descriptions bordered column={2}>
                    <Descriptions.Item label={<Space><DollarOutlined />Phí khám</Space>}>
                      {detail.consultationFee ? (
                        <Text strong>{detail.consultationFee.toLocaleString()} VNĐ</Text>
                      ) : (
                        <Text type="secondary">Chưa có thông tin</Text>
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label={<Space><TeamOutlined />Số bác sĩ</Space>}>
                      {detail.doctorCount || <Text type="secondary">Chưa có thông tin</Text>}
                    </Descriptions.Item>

                    <Descriptions.Item label="Số y tá">
                      {detail.nurseCount || <Text type="secondary">Chưa có thông tin</Text>}
                    </Descriptions.Item>

                    <Descriptions.Item label="Chứng chỉ" span={2}>
                      {detail.certifications && detail.certifications.length > 0 ? (
                        <Space wrap>
                          {detail.certifications.map((cert, index) => (
                            <Tag key={index} color="gold">{cert}</Tag>
                          ))}
                        </Space>
                      ) : (
                        <Text type="secondary">Chưa có thông tin</Text>
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Giờ làm việc" key="workingHours">
            <Card title="Thời gian hoạt động" extra={<ClockCircleOutlined />}>
              {renderWorkingHours()}
            </Card>
          </TabPane>

          <TabPane tab="Hình ảnh" key="gallery">
            <Card title="Thư viện ảnh">
              {detail.images && detail.images.length > 0 ? (
                <Image.PreviewGroup>
                  <Row gutter={[16, 16]}>
                    {detail.images.map((image, index) => (
                      <Col key={index} xs={12} sm={8} md={6} lg={4}>
                        <Image
                          src={image}
                          alt={`${detail.placeName} - ${index + 1}`}
                          style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 4 }}
                        />
                      </Col>
                    ))}
                  </Row>
                </Image.PreviewGroup>
              ) : (
                <Empty description="Chưa có hình ảnh" />
              )}
            </Card>
          </TabPane>

          <TabPane tab="Vị trí" key="location">
            <Card title="Thông tin vị trí">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Vĩ độ">
                  {detail.location?.coordinates?.[1] || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Kinh độ">
                  {detail.location?.coordinates?.[0] || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Khoảng cách" span={2}>
                  {detail.distance ? `${detail.distance.toFixed(2)} km` : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default HealthDetail;
